import { isDefined } from "@clipboard-health/util-ts";
import { isEqual, parseISO } from "date-fns";

function toDate(value: string | Date): Date {
  return typeof value === "string" ? parseISO(value) : value;
}

/**
 * Detects whether a provisional extra worked time request represents a clock-in
 * adjustment by comparing the request's start time against the original clock-in time.
 *
 * Works for both new requests (with `reasons.start`) and legacy requests created before
 * the structured `reasons` field was introduced.
 */
export function isClockInAdjustment(
  requestStart: string | Date,
  originalClockInTime: string | Date
): boolean {
  return !isEqual(toDate(requestStart), toDate(originalClockInTime));
}

/**
 * Detects whether a provisional extra worked time request represents a clock-out
 * adjustment by comparing the request's end time against the original clock-out time.
 *
 * Returns false when the request has no `end` (i.e. clock-in-only adjustments).
 */
export function isClockOutAdjustment(
  requestEnd: string | Date | undefined,
  originalClockOutTime: string | Date | undefined
): boolean {
  if (!isDefined(requestEnd) || !isDefined(originalClockOutTime)) {
    return false;
  }

  return !isEqual(toDate(requestEnd), toDate(originalClockOutTime));
}

/**
 * Detects whether a provisional extra worked time request includes a shift extension
 * by checking for the presence of a structured `reasons.end` value.
 */
export function isShiftExtension(reasons: { end?: string } | undefined): boolean {
  return isDefined(reasons?.end);
}
